import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { dataGridPremiumColumns } from '../../utils/helper';

import {
  DataGridIdentifiers,
  FormErrorUI,
  AutoCompleteSelectUI,
  CheckboxUI,
  AddButtonUi,
  DatagridPremiumUi,
  InputUI,
} from '../Interface';

import useWindowSubtractedHeight from '../../hooks/useWindowSubtractedHeight';

function EditToolbar(props) {
  const { disabled, push, defaultMaterial } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    const newData = {
      ...defaultMaterial,
      id: uuidv4(),
    };

    await push(newData);

    const fieldsToValidate = [
      `serviceMaterial.${values.serviceMaterial.length ?? 0}.itemCode`,
      `serviceMaterial.${values.serviceMaterial.length ?? 0}.qty`,
      `serviceMaterial.${values.serviceMaterial.length ?? 0}.note`,
      `serviceMaterial.${values.serviceMaterial.length ?? 0}.isRequired`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
}

const ServiceOperationMaterial = ({ disabled, defaultMaterial, autoCompleteOptions, autocompleteData }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const gridHeight = useWindowSubtractedHeight({ otherContentHeight: 680, minHeight: 400 });

  const getValueIndex = (values, id) => {
    return values.serviceMaterial.findIndex((rc) => rc.id === id);
  };

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridPremiumColumns([
      {
        field: 'itemCode',
        headerName: 'Cikkszám',
        type: 'string',
        editable: true,
        renderCell: (data) => {
          const findedItem = autoCompleteOptions?.items.find((item) => item.value === data.row?.itemCode);
          return findedItem?.label;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedItem = autoCompleteOptions.items.find((item) => item.value === data.row.itemCode);

          return (
            <Field
              name={`serviceMaterial.${valueIndex}.itemCode`}
              component={AutoCompleteSelectUI}
              helperText={<FormErrorUI message={errors?.serviceMaterial?.[valueIndex]?.itemCode} />}
              selectedValue={values.serviceMaterial?.[valueIndex]?.itemCode}
              selectedLabelValue={findedItem?.label}
              onChange={(_e, newVal) => {
                setFieldValue(`serviceMaterial.${valueIndex}.itemCode`, newVal?.value ?? null);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal.value ?? null,
                });
              }}
              dataset={autoCompleteOptions.items}
              onBlur={async () => await validateField(`serviceMaterial.${valueIndex}.itemCode`)}
            />
          );
        },
      },
      {
        field: 'qty',
        headerName: 'Mennyiség',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`serviceMaterial.${valueIndex}.qty`}
              type="number"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.serviceMaterial?.[valueIndex]?.qty} />}
              onChange={(e) => {
                setFieldValue(`serviceMaterial.${valueIndex}.qty`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`serviceMaterial.${valueIndex}.qty`)}
            />
          );
        },
      },
      {
        field: 'quantityUnitId',
        headerName: 'Me.',
        type: 'string',
        editable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedI = autocompleteData.items.find(
            (i) => i.itemCode === values.serviceMaterial?.[valueIndex]?.itemCode
          );

          const findedUnit = autocompleteData.quantityUnit.find((u) => u.id === findedI?.quantityUnitId);
          return findedUnit?.code ?? '';
        },
      },
      {
        field: 'note',
        headerName: 'Megjegyzés',
        type: 'string',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`serviceMaterial.${valueIndex}.note`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.serviceMaterial?.[valueIndex]?.note} />}
              onChange={(e) => {
                setFieldValue(`serviceMaterial.${valueIndex}.note`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`serviceMaterial.${valueIndex}.note`)}
            />
          );
        },
      },
      {
        field: 'isRequired',
        type: 'boolean',
        headerName: 'Kötelező elem',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name={`serviceMaterial.${valueIndex}.isRequired`}
              component={CheckboxUI}
              helperText={<FormErrorUI message={errors?.serviceMaterial?.[valueIndex]?.isRequired} />}
              checked={data.row?.isRequired === true}
              variant="standard"
              onChange={(e) => {
                setFieldValue(`serviceMaterial.${valueIndex}.isRequired`, e.target.checked ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.checked,
                });
              }}
              onBlur={async () => await validateField(`serviceMaterial.${valueIndex}.isRequired`)}
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);
  };

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="serviceMaterial">
            {({ remove, push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.ServiceOperationMaterial}
                height={gridHeight}
                columns={columns(setFieldValue, validateField, errors, remove)}
                rows={values?.serviceMaterial ?? []}
                slots={{
                  toolbar: EditToolbar,
                }}
                headerFilters={false}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    defaultMaterial,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                editMode="row"
                getRowHeight={() => 'auto'}
                onCellClick={(params, event) => {
                  if (disabled) return;
                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default ServiceOperationMaterial;
